import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { authAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Card, Button, PageHeader, Badge } from '../components/ui';
import { INDIA_STATES } from '../utils/constants';
import { subscribeToPush, unsubscribeFromPush, isPushSubscribed, getNotificationPermission } from '../services/pushNotificationService';
import toast from 'react-hot-toast';

const inp = { width: '100%', padding: '8px 12px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 14, fontFamily: 'inherit', outline: 'none', background: '#fff', boxSizing: 'border-box' };
const sel = { ...inp, cursor: 'pointer' };

function getPasswordStrength(pw) {
  if (!pw) return null;
  const len = pw.length;
  const hasUpper = /[A-Z]/.test(pw);
  const hasNum   = /[0-9]/.test(pw);
  const hasSpec  = /[^A-Za-z0-9]/.test(pw);
  if (len < 8)                    return { label: 'Weak',   color: '#ef4444', width: '25%' };
  if (!hasUpper || !hasNum)       return { label: 'Fair',   color: '#f97316', width: '50%' };
  if (len < 12)                   return { label: 'Good',   color: '#eab308', width: '75%' };
  if (hasUpper && hasNum && hasSpec) return { label: 'Strong', color: '#22c55e', width: '100%' };
  return { label: 'Good',   color: '#eab308', width: '75%' };
}

function PwInput({ value, onChange, placeholder }) {
  const [show, setShow] = useState(false);
  return (
    <div style={{ position: 'relative' }}>
      <input type={show ? 'text' : 'password'} value={value} onChange={onChange}
        placeholder={placeholder || '••••••••'}
        style={{ ...inp, paddingRight: 40 }} />
      <button type="button" onClick={() => setShow(s => !s)}
        style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', border: 'none', background: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: 16 }}>
        {show ? '🙈' : '👁️'}
      </button>
    </div>
  );
}

export default function ProfilePage() {
  const { user, updateUser, logout } = useAuth();
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [pwSaving, setPwSaving] = useState(false);
  const [form, setForm] = useState({ name: user?.name || '' });
  const [pwForm, setPwForm] = useState({ current_password: '', new_password: '', confirm: '' });

  /* Push notification state */
  const [pushSupported, setPushSupported] = useState(false);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushLoading, setPushLoading] = useState(false);
  const [testLoading, setTestLoading] = useState(false);

  useEffect(() => {
    const supported = 'Notification' in window && 'serviceWorker' in navigator && 'PushManager' in window;
    setPushSupported(supported);
    if (supported) {
      isPushSubscribed().then(setPushEnabled);
    }
  }, []);

  const handlePushToggle = async () => {
    setPushLoading(true);
    try {
      if (pushEnabled) {
        await unsubscribeFromPush();
        setPushEnabled(false);
        toast.success('Push notifications disabled');
      } else {
        const result = await subscribeToPush();
        if (result === 'granted') { setPushEnabled(true); toast.success('Push notifications enabled'); }
        else if (result === 'denied') toast.error('Permission denied — enable in browser settings');
        else if (result === 'unsupported') toast.error('Not supported on this device/browser');
        else toast.error('Failed to enable push notifications');
      }
    } catch {
      toast.error('Something went wrong');
    } finally {
      setPushLoading(false);
    }
  };

  const sendTestNotification = async () => {
    setTestLoading(true);
    try {
      const apiBase = process.env.REACT_APP_API_URL || '';
      await axios.post(`${apiBase}/api/push/test`);
      toast.success('Test notification sent!');
    } catch {
      toast.error('Failed to send test notification');
    } finally {
      setTestLoading(false);
    }
  };

  const saveProfile = async () => {
    if (!form.name.trim()) { toast.error('Name required'); return; }
    setSaving(true);
    try {
      const res = await authAPI.updateProfile(form);
      updateUser(res.data?.data || {});
      toast.success('Profile updated');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Update failed');
    } finally { setSaving(false); }
  };

  const changePassword = async () => {
    if (!pwForm.current_password || !pwForm.new_password) { toast.error('All fields required'); return; }
    if (pwForm.new_password !== pwForm.confirm) { toast.error('Passwords do not match'); return; }
    if (pwForm.new_password.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    if (!/[A-Z]/.test(pwForm.new_password)) { toast.error('Password must contain at least one uppercase letter'); return; }
    if (!/[0-9]/.test(pwForm.new_password)) { toast.error('Password must contain at least one number'); return; }
    setPwSaving(true);
    try {
      await authAPI.changePassword({ current_password: pwForm.current_password, new_password: pwForm.new_password, confirm_password: pwForm.confirm });
      toast.success('Password updated successfully — please log in again');
      setPwForm({ current_password: '', new_password: '', confirm: '' });
      setTimeout(() => { logout(); navigate('/login'); }, 1500);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Password change failed');
    } finally { setPwSaving(false); }
  };

  const strength = getPasswordStrength(pwForm.new_password);

  const ROLE_COLORS = { super_admin: 'purple', admin: 'navy', internal_team: 'blue' };

  return (
    <div style={{ maxWidth: 580 }}>
      <PageHeader title="My Profile" subtitle="Manage your account settings" />

      {/* Avatar & info */}
      <Card style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20 }}>
          <div style={{ width: 64, height: 64, background: '#1B2A6B', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, fontWeight: 700, color: '#C9A84C', flexShrink: 0 }}>
            {user?.name?.[0]?.toUpperCase()}
          </div>
          <div>
            <h2 style={{ fontSize: 18, fontWeight: 800, color: '#1B2A6B' }}>{user?.name}</h2>
            <p style={{ fontSize: 13, color: '#64748b', marginBottom: 6 }}>{user?.email}</p>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              <Badge color={ROLE_COLORS[user?.role] || 'gray'}>{user?.role?.replace('_', ' ')}</Badge>
              {user?.sub_role && <Badge color="gold">{user.sub_role}</Badge>}
              {user?.sub_role === 'SVP' && user?.regions?.length > 0
                ? user.regions.map(r => <Badge key={r} color="gray">📍 {r}</Badge>)
                : user?.region && <Badge color="gray">📍 {user.region}</Badge>
              }
            </div>
          </div>
        </div>

        <h3 style={{ fontSize: 14, fontWeight: 700, color: '#1B2A6B', marginBottom: 12 }}>Update Profile</h3>
        <div style={{ display: 'grid', gap: 12 }}>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Full Name</label>
            <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} style={inp} />
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Region</label>
            <div style={{ ...inp, background: '#f8fafc', color: '#64748b', cursor: 'not-allowed', minHeight: 38, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6 }}>
              {user?.sub_role === 'SVP' && user?.regions?.length > 0
                ? user.regions.map(r => (
                    <span key={r} style={{ background: '#e2e8f0', borderRadius: 5, padding: '2px 8px', fontSize: 12, fontWeight: 600 }}>{r}</span>
                  ))
                : (user?.region || 'All India')
              }
            </div>
            <p style={{ fontSize: 11, color: '#94a3b8', marginTop: 3 }}>Region is managed by your administrator.</p>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button variant="primary" onClick={saveProfile} disabled={saving}>{saving ? 'Saving…' : 'Save Changes'}</Button>
          </div>
        </div>
      </Card>

      {/* Change Password */}
      <Card>
        <h3 style={{ fontSize: 14, fontWeight: 700, color: '#1B2A6B', marginBottom: 12 }}>Change Password</h3>
        <div style={{ display: 'grid', gap: 12 }}>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Current Password</label>
            <PwInput value={pwForm.current_password} onChange={e => setPwForm(f => ({ ...f, current_password: e.target.value }))} />
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>New Password</label>
            <PwInput value={pwForm.new_password} onChange={e => setPwForm(f => ({ ...f, new_password: e.target.value }))} placeholder="Min 8 chars, uppercase, number" />
            {/* Strength indicator */}
            {strength && (
              <div style={{ marginTop: 6 }}>
                <div style={{ height: 4, background: '#f1f5f9', borderRadius: 4, overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: strength.width, background: strength.color, transition: 'width 0.3s, background 0.3s', borderRadius: 4 }} />
                </div>
                <span style={{ fontSize: 11, color: strength.color, fontWeight: 700 }}>{strength.label}</span>
              </div>
            )}
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Confirm New Password</label>
            <PwInput value={pwForm.confirm} onChange={e => setPwForm(f => ({ ...f, confirm: e.target.value }))} />
            {pwForm.confirm && pwForm.new_password !== pwForm.confirm && (
              <p style={{ fontSize: 11, color: '#ef4444', marginTop: 2 }}>Passwords do not match</p>
            )}
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button variant="primary" onClick={changePassword} disabled={pwSaving}>{pwSaving ? 'Updating…' : 'Update Password'}</Button>
          </div>
        </div>
      </Card>

      {/* Notification Settings */}
      <Card style={{ marginTop: 20 }}>
        <h3 style={{ fontSize: 14, fontWeight: 700, color: '#1B2A6B', marginBottom: 16 }}>
          🔔 Notification Settings
        </h3>

        {!pushSupported ? (
          <div style={{ fontSize: 13, color: '#94a3b8', padding: '12px 0' }}>
            Push notifications are not supported on this browser.
          </div>
        ) : (
          <div style={{ display: 'grid', gap: 16 }}>
            {/* Toggle row */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid #f1f5f9' }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 600, color: '#1e293b' }}>Push Notifications</div>
                <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>
                  {getNotificationPermission() === 'denied'
                    ? 'Blocked by browser — enable in site settings'
                    : pushEnabled ? 'Receiving alerts on this device' : 'Disabled on this device'}
                </div>
              </div>
              {/* Toggle switch */}
              <button
                onClick={handlePushToggle}
                disabled={pushLoading || getNotificationPermission() === 'denied'}
                style={{
                  width: 44, height: 24, borderRadius: 12, border: 'none', cursor: 'pointer',
                  background: pushEnabled ? '#1B2A6B' : '#cbd5e1',
                  position: 'relative', transition: 'background 0.2s',
                  opacity: pushLoading ? 0.6 : 1,
                  flexShrink: 0,
                }}
                aria-label={pushEnabled ? 'Disable push notifications' : 'Enable push notifications'}
              >
                <span style={{
                  position: 'absolute', top: 3, left: pushEnabled ? 23 : 3,
                  width: 18, height: 18, borderRadius: '50%', background: '#fff',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.2)', transition: 'left 0.2s',
                }} />
              </button>
            </div>

            {/* Status indicator */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
              <span style={{
                width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                background: pushEnabled ? '#22c55e' : '#cbd5e1',
              }} />
              <span style={{ color: '#64748b' }}>
                Status: <strong style={{ color: pushEnabled ? '#22c55e' : '#94a3b8' }}>
                  {getNotificationPermission() === 'denied' ? 'Blocked' : pushEnabled ? 'Enabled' : 'Disabled'}
                </strong>
              </span>
            </div>

            {/* Test button — only if enabled */}
            {pushEnabled && (
              <div>
                <Button variant="secondary" onClick={sendTestNotification} disabled={testLoading}>
                  {testLoading ? 'Sending…' : 'Send Test Notification'}
                </Button>
                <p style={{ fontSize: 11, color: '#94a3b8', marginTop: 6 }}>
                  Sends a test push to verify notifications are working.
                </p>
              </div>
            )}

            {/* What you'll receive */}
            <div style={{ background: '#f8fafc', borderRadius: 10, padding: '12px 14px' }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#475569', marginBottom: 8 }}>You'll be notified about:</div>
              {[
                ['🎯', 'New lead assigned to you'],
                ['📅', 'Follow-up reminders due today (8 AM)'],
                ['🎬', 'Demo reminders — 24 hours and 1 hour before'],
              ].map(([icon, text]) => (
                <div key={text} style={{ display: 'flex', gap: 8, fontSize: 12, color: '#64748b', marginBottom: 4 }}>
                  <span>{icon}</span><span>{text}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </Card>

      {/* Account Info */}
      <Card style={{ marginTop: 20, background: '#f8fafc' }}>
        <h3 style={{ fontSize: 13, fontWeight: 700, color: '#64748b', marginBottom: 10 }}>Account Information</h3>
        <div style={{ display: 'grid', gap: 4 }}>
          {[
            ['Last Login', user?.last_login ? new Date(user.last_login).toLocaleString('en-IN') : 'First login'],
            ['Account Status', 'Active'],
            ['Organization', 'Nova Era Labs'],
          ].map(([k, v]) => (
            <div key={k} style={{ display: 'flex', gap: 8, fontSize: 12 }}>
              <span style={{ color: '#94a3b8', width: 120, flexShrink: 0 }}>{k}</span>
              <span style={{ color: '#475569' }}>{v}</span>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
